import React, { useState, useEffect } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import ConfirmModal from "components/confirm-modal";
import ErrorTag from "components/error-tag";
import { useOrchidPubNubState } from "contexts/pubnub/OrchidPubNubStateContext";
import styles from "./styles.module.scss";
import { AmplitudePageName, track } from "utils/amplitude";

const AMPLITUDE_COMPONENT_NAME = "send-reminder-modal";

const SendAReminder = ({
  show,
  onHide,
  patientUserId,
  form,
  amplitudePageName,
}: {
  patientUserId: string;
  show: boolean;
  onHide: () => void;
  form: Partial<FormMetaData>;
  amplitudePageName: AmplitudePageName;
}) => {
  const { chatOperations } = useOrchidPubNubState();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(
    `Please complete "${form?.form_schema_title}" ASAP!`,
  );
  const [showRequestSentModal, setShowRequestSentModal] = useState(false);
  const [error, setError] = useState<any>();

  useEffect(() => {
    if (!patientUserId) return;
    if (chatOperations) {
      chatOperations.addPartner(patientUserId);
    }
  }, [patientUserId, chatOperations]);

  useEffect(() => {
    if (form) {
      setMessage(`Please complete "${form.form_schema_title}" ASAP!`);
    }
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);

    try {
      await chatOperations.sendMessage(patientUserId, message);
      setShowRequestSentModal(true);
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
      onHide();
      setMessage(`Please complete "${form?.form_schema_title}" ASAP!`);
    }
  };

  return (
    <>
      {error && (
        <ErrorTag
          show={true}
          onHide={() => setError(undefined)}
          duration={3000}
          errorMsg={error.toString()}
        />
      )}
      {/* reminder sent */}
      <ConfirmModal
        labelTitle={"Sent!"}
        children={"You can view the reminder form the chat tab."}
        hideCancel={true}
        labelConfirm={"Got it"}
        show={showRequestSentModal}
        onCancel={() => setShowRequestSentModal(false)}
        onConfirm={() => setShowRequestSentModal(false)}
      />
      <ModalLayout
        dialogClassName={styles["send-reminder__dialog"]}
        contentClassName={styles["send-reminder__content"]}
        show={show}
        onHide={() => {
          track(
            amplitudePageName,
            [AMPLITUDE_COMPONENT_NAME, "exit-modal-button"],
            "click",
          );
          onHide();
          setMessage(`Please complete "${form?.form_schema_title}" ASAP!`);
        }}
        Header={"Send a reminder via chat?"}
        headerClass={styles["send-reminder__header"]}
        bodyClass={styles["send-reminder__body"]}
        Footer={
          <>
            <Button
              variant="link"
              className="gray01"
              onClick={() => {
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "cancel-button"],
                  "click",
                );
                onHide();
                setMessage(
                  `Please complete "${form?.form_schema_title}" ASAP!`,
                );
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={busy}
              type="submit"
              form="send-reminder"
              onClick={() => {
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "send-button"],
                  "click",
                );
              }}
            >
              Send
              {busy && (
                <Spinner
                  as="span"
                  animation="grow"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              )}
            </Button>
          </>
        }
        footerClass={styles["send-reminder__footer"]}
      >
        <p>
          We will send below message to your patient to remind the patients to
          complete the assignments.
        </p>
        <Form id="send-reminder" onSubmit={handleSubmit}>
          <Form.Group className="mb-0" controlId="message">
            <Form.Control
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onFocus={() => {
                track(
                  amplitudePageName,
                  [AMPLITUDE_COMPONENT_NAME, "message-field"],
                  "focus",
                );
              }}
            />
          </Form.Group>
        </Form>
      </ModalLayout>
    </>
  );
};

export default SendAReminder;
